/**
 * Multipart-upload helper using XMLHttpRequest so we get real upload
 * progress events (which `fetch` doesn't expose in any browser today).
 *
 * Usage:
 *   const result = await uploadFile<EntryAttachmentResponse>(url, file, {
 *     onProgress: (pct) => setStatus({ kind: 'uploading', pct }),
 *     signal: abortController.signal,
 *   });
 *
 * The helper sends the file as the `file` form field. Cookies are
 * included by default so the signed session cookie flows like a normal
 * authenticated request.
 */

export interface UploadFileOptions {
  /** Called with the upload progress percentage 0..100. Fired multiple times. */
  onProgress?: (pct: number) => void;
  /** Abort the upload. Resulting promise rejects with `AbortError`. */
  signal?: AbortSignal;
  /**
   * Override the form field name. Defaults to `'file'` (matches the
   * backend's `req.file()` reader).
   */
  fieldName?: string;
}

export class UploadError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'UploadError';
    this.status = status;
    this.body = body;
  }
}

export function uploadFile<T = unknown>(
  url: string,
  file: File,
  opts: UploadFileOptions = {},
): Promise<T> {
  const fieldName = opts.fieldName ?? 'file';
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true; // include signed session cookie
    xhr.responseType = 'text';

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) {
        const pct = Math.min(100, Math.max(0, Math.round((e.loaded / e.total) * 100)));
        opts.onProgress(pct);
      }
    };

    xhr.onload = () => {
      const status = xhr.status;
      const contentType = xhr.getResponseHeader('content-type') ?? '';
      let body: unknown = xhr.responseText;
      if (contentType.includes('application/json')) {
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          // leave as raw text
        }
      }
      if (status >= 200 && status < 300) {
        resolve(body as T);
      } else {
        const message =
          typeof body === 'object' && body !== null && 'message' in body
            ? String((body as { message: unknown }).message)
            : `Upload failed with status ${status}`;
        reject(new UploadError(status, message, body));
      }
    };

    xhr.onerror = () => {
      reject(new UploadError(0, 'Network error during upload', null));
    };
    xhr.ontimeout = () => {
      reject(new UploadError(0, 'Upload timed out', null));
    };
    xhr.onabort = () => {
      const e = new Error('Upload aborted');
      e.name = 'AbortError';
      reject(e);
    };

    if (opts.signal) {
      if (opts.signal.aborted) {
        // Abort immediately and don't even fire the request.
        xhr.abort();
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    const fd = new FormData();
    fd.append(fieldName, file, file.name);
    xhr.send(fd);
  });
}
